import React from 'react';
import { Tooltip } from '@mui/material';
import {
  MUIDataTableColumn,
  MUIDataTableColumnDef,
  DisplayData,
} from 'mui-datatables';
import type { TFunction } from 'i18next';
import { DataTableStatisticDto } from './Dto/DataTableStatisticDto';
import { StyledIconButton } from './StatisticsDataTable.styles';
import { CloudDownload } from '@mui/icons-material';

type RowShape = { data: any[]; dataIndex: number };

const downloadCsv = (csv: string, filename: string) => {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.setAttribute('download', filename.endsWith('.csv') ? filename : `${filename}.csv`);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const normalizeColumns = (cols: MUIDataTableColumnDef[]): MUIDataTableColumn[] =>
  cols.map((c) =>
    typeof c === 'string'
      ? ({ name: c, label: c, options: {} } as MUIDataTableColumn)
      : c
  );

const isDisplayed = (c: MUIDataTableColumn) => {
  const d = c.options?.display;
  return d !== 'excluded' && d !== false;
};

export const CustomStatisticsToolbar: React.FC<{
  selectedRows: { data: Array<{ index: number; dataIndex: number }> };
  displayData: DisplayData;
  adjustedData: DataTableStatisticDto[];
  columns: MUIDataTableColumnDef[];
  t: TFunction;
  buildStatisticsCsv: (
    t: TFunction,
    columns: MUIDataTableColumn[],
    data: RowShape[],
    opts?: { separator?: string; completedColumn?: string; pointsColumn?: string }
  ) => string;
  generateFileName: () => string;
  meta: { completedColumn?: string; pointsColumn?: string; separator?: string };
}> = ({
  selectedRows,
  displayData,
  adjustedData,
  columns,
  t,
  buildStatisticsCsv,
  generateFileName,
  meta,
}) => {
  const normalized = React.useMemo(() => normalizeColumns(columns), [columns]);
  const displayedColumns = React.useMemo(
    () => normalized.filter(isDisplayed),
    [normalized]
  );

  const buildSelectedRowShapes = (): RowShape[] => {
    return selectedRows.data.map(({ dataIndex }) => {
      const original = adjustedData[dataIndex] as any;
      const values = displayedColumns.map((col) => {
        const fullIdx = normalized.indexOf(col);
        if (Array.isArray(original)) {
          return original[fullIdx];
        }
        return original?.[col.name] ?? '';
      });

      return { data: values, dataIndex };
    });
  };

  const handleExportSelected = () => {
    const rowShapes = buildSelectedRowShapes();
    const csv = buildStatisticsCsv(t, displayedColumns, rowShapes, meta);
    downloadCsv(csv, generateFileName());
  };

  return (
    <Tooltip title={t('dataTable.downloadSelectedCsv')}>
      <StyledIconButton aria-label="download-selected" onClick={handleExportSelected} >
        <CloudDownload />
      </StyledIconButton>
    </Tooltip>
  );
};